import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import LoadingSpinner from '../../common/LoadingSpinner.jsx';
import { buildApiUrl } from '../../../utils/apiBase.js';
import { useToast } from '../../../contexts/ToastContext.jsx';
import {
  canManageClinic,
  HUB_CLINIC_EDIT_DENIED_MESSAGE,
} from '../../../utils/hubPermissions.js';

const DEFAULT_VERIFY_FAIL =
  '連携先に接続できませんでした。入力内容を確認してください。';
const DEFAULT_UNAVAILABLE =
  '現在この機能は利用できません。しばらく経ってから再度お試しください。';
const DEFAULT_NETWORK =
  '通信に失敗しました。ネットワーク接続を確認して再度お試しください。';
const MSG_SAVE_SUCCESS = '連携を保存しました';
const MSG_DELETE_SUCCESS = '連携を解除しました。';
const MSG_DELETE_NONE = '連携設定はありませんでした。';
const MSG_LOAD_FAILED = '連携情報の取得に失敗しました。';
const MSG_SESSION = 'セッションの有効期限が切れています。再度ログインしてください。';
const SUPPORTED_FIELD_TYPES = new Set(['text', 'password', 'select']);

function isEndUserJapaneseMessage(message) {
  if (!message || typeof message !== 'string') return false;
  if (message.length > 180) return false;
  if (!/[\u3040-\u30ff\u3400-\u9fff]/.test(message)) return false;
  if (/stack|exception|TypeError|ReferenceError|at\s+\w+\s*\(|ENOENT|ECONNREFUSED|firebase|Bearer|Authorization|CORS|Origin|API\s*エラー|internal/i.test(message)) {
    return false;
  }
  return true;
}

function integrationStatusLabel(state) {
  if (!state?.configured) return '未連携';
  if (state.enabled === false) return '無効';
  if (state.status === 'verified') return '接続確認済み';
  return '設定済み';
}

function messageForSaveFailure(status, data) {
  if (status === 503 || data?.error === 'integration-unavailable') {
    return isEndUserJapaneseMessage(data?.message) ? data.message : DEFAULT_UNAVAILABLE;
  }
  if (status === 502 || data?.error === 'integration-network-error') {
    return isEndUserJapaneseMessage(data?.message) ? data.message : DEFAULT_NETWORK;
  }
  if (status === 403) return HUB_CLINIC_EDIT_DENIED_MESSAGE;
  if (status === 401) return MSG_SESSION;
  if (isEndUserJapaneseMessage(data?.message)) return data.message;
  return DEFAULT_VERIFY_FAIL;
}

function messageForCaughtError(err, fallback) {
  const msg = err?.message || '';
  if (/failed to fetch|networkerror|load failed|network request failed/i.test(msg)) {
    return DEFAULT_NETWORK;
  }
  if (isEndUserJapaneseMessage(msg)) return msg;
  return fallback;
}

function fieldPlaceholder(field, configured) {
  if (configured && field.placeholderWhenConfigured) return field.placeholderWhenConfigured;
  return field.placeholder || '';
}

/**
 * アプリ連携設定（カタログ API 駆動）
 */
const AppIntegrations = () => {
  const { user } = useOutletContext();
  const { showToast } = useToast();
  const canManage = canManageClinic(user);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [disconnectingId, setDisconnectingId] = useState(null);
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [providers, setProviders] = useState([]);
  const [drafts, setDrafts] = useState({});

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    setError('');
    try {
      const res = await fetch(buildApiUrl('/api/clinic/integrations'), {
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 401) {
          throw new Error(MSG_SESSION);
        }
        throw new Error(
          isEndUserJapaneseMessage(data.message) ? data.message : MSG_LOAD_FAILED
        );
      }
      const list = Array.isArray(data.providers) ? data.providers : [];
      setProviders(list);
      const nextDrafts = {};
      for (const p of list) {
        const values = {};
        for (const field of p.fields || []) {
          if (field.secret) {
            values[field.key] = '';
          } else {
            values[field.key] = p.state?.displayValues?.[field.key] || '';
          }
        }
        nextDrafts[p.id] = values;
      }
      setDrafts(nextDrafts);
    } catch (err) {
      setLoadError(messageForCaughtError(err, MSG_LOAD_FAILED));
      setProviders([]);
      setDrafts({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const busy = savingId != null || disconnectingId != null;

  const handleDraftChange = (providerId, key, value) => {
    setDrafts((prev) => ({
      ...prev,
      [providerId]: { ...(prev[providerId] || {}), [key]: value },
    }));
  };

  const handleSave = async (provider, e) => {
    e.preventDefault();
    if (!canManage) {
      showToast('integrations.save.error', { message: HUB_CLINIC_EDIT_DENIED_MESSAGE });
      return;
    }

    setSavingId(provider.id);
    setError('');
    setSuccess('');

    try {
      const fieldValues = { ...(drafts[provider.id] || {}) };
      const res = await fetch(buildApiUrl(`/api/clinic/integrations/${encodeURIComponent(provider.id)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ fields: fieldValues }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const message = messageForSaveFailure(res.status, data);
        setError(message);
        showToast('integrations.save.error', { message });
        return;
      }

      const updated = data.provider;
      setProviders((prev) => prev.map((p) => (p.id === provider.id ? updated : p)));
      setDrafts((prev) => {
        const next = { ...(prev[provider.id] || {}) };
        for (const field of updated.fields || []) {
          if (field.secret) next[field.key] = '';
          else next[field.key] = updated.state?.displayValues?.[field.key] || next[field.key] || '';
        }
        return { ...prev, [provider.id]: next };
      });
      setSuccess(`${updated.title || provider.title}: ${MSG_SAVE_SUCCESS}`);
      showToast('integrations.save.success');
    } catch (err) {
      const message = messageForCaughtError(err, DEFAULT_NETWORK);
      setError(message);
      showToast('integrations.save.error', { message });
    } finally {
      setSavingId(null);
    }
  };

  const handleConfirmDisconnect = async () => {
    if (!canManage || !disconnectTarget) return;
    const provider = disconnectTarget;

    setDisconnectingId(provider.id);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(
        buildApiUrl(`/api/clinic/integrations/${encodeURIComponent(provider.id)}`),
        { method: 'DELETE', credentials: 'include' }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) throw new Error(HUB_CLINIC_EDIT_DENIED_MESSAGE);
        throw new Error(
          isEndUserJapaneseMessage(data.message) ? data.message : '連携の解除に失敗しました。'
        );
      }
      const successMessage =
        data.wasConfigured === false ? MSG_DELETE_NONE : MSG_DELETE_SUCCESS;
      const updated = data.provider;
      setProviders((prev) => prev.map((p) => (p.id === provider.id ? updated : p)));
      setDrafts((prev) => {
        const cleared = {};
        for (const field of (updated.fields || provider.fields || [])) {
          cleared[field.key] = '';
        }
        return { ...prev, [provider.id]: cleared };
      });
      setSuccess(`${provider.title}: ${successMessage}`);
      showToast({ eventId: 'integrations.delete.success', message: successMessage });
      setDisconnectTarget(null);
    } catch (err) {
      const message = messageForCaughtError(err, '連携の解除に失敗しました。');
      setError(message);
      showToast('integrations.delete.error', { message });
    } finally {
      setDisconnectingId(null);
    }
  };

  const providerCards = useMemo(() => providers, [providers]);

  return (
    <>
      <div className="hub-main-header">
        <h1 className="hub-page-title">アプリ連携</h1>
      </div>

      <div className="hub-main-content">
        {!canManage && (
          <div style={{
            background: 'var(--hub-warning-light)',
            padding: 'var(--hub-space-md)',
            borderRadius: 'var(--hub-radius-md)',
            marginBottom: 'var(--hub-space-xl)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--hub-space-sm)',
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20, color: 'var(--hub-warning)' }}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <span style={{ color: 'var(--hub-text-primary)' }}>
              {HUB_CLINIC_EDIT_DENIED_MESSAGE}
            </span>
          </div>
        )}

        {success && (
          <div style={{
            background: 'var(--hub-success-light)',
            color: 'var(--hub-success)',
            padding: 'var(--hub-space-md)',
            borderRadius: 'var(--hub-radius-md)',
            marginBottom: 'var(--hub-space-xl)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--hub-space-sm)',
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20 }}>
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            {success}
          </div>
        )}

        {error && (
          <div
            role="alert"
            style={{
              background: 'var(--hub-danger-light)',
              color: 'var(--hub-danger)',
              padding: 'var(--hub-space-md)',
              borderRadius: 'var(--hub-radius-md)',
              marginBottom: 'var(--hub-space-xl)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--hub-space-sm)',
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20 }}>
              <circle cx="12" cy="12" r="9"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            {error}
          </div>
        )}

        {loading ? (
          <div className="hub-card hub-enter-panel animate-fade-in-up stagger-1" style={{ marginBottom: 'var(--hub-space-xl)' }}>
            <div className="hub-shimmer" style={{ width: '140px', height: '1.25rem', marginBottom: '10px' }} />
            <div className="hub-shimmer" style={{ width: '90%', height: '0.875rem', marginBottom: '6px' }} />
            <div className="hub-shimmer" style={{ width: '100%', height: '42px' }} />
          </div>
        ) : loadError ? (
          <div className="hub-card" style={{ marginBottom: 'var(--hub-space-xl)' }}>
            <div role="alert" style={{
              background: 'var(--hub-danger-light)',
              color: 'var(--hub-danger)',
              padding: 'var(--hub-space-md)',
              borderRadius: 'var(--hub-radius-md)',
              marginBottom: 'var(--hub-space-lg)',
            }}>
              {loadError}
            </div>
            <button type="button" className="hub-btn hub-btn-primary" onClick={loadCatalog}>
              再読み込み
            </button>
          </div>
        ) : providerCards.length === 0 ? (
          <div className="hub-card" style={{ marginBottom: 'var(--hub-space-xl)' }}>
            <p style={{ color: 'var(--hub-text-muted)', margin: 0 }}>
              利用可能な連携はありません。
            </p>
          </div>
        ) : (
          providerCards.map((provider, index) => {
            const configured = Boolean(provider.state?.configured);
            const statusLabel = integrationStatusLabel(provider.state);
            const statusOk = configured && provider.state?.enabled !== false && provider.state?.status === 'verified';
            const statusWarn = configured && (provider.state?.enabled === false || provider.state?.status !== 'verified');
            const draft = drafts[provider.id] || {};
            const isSaving = savingId === provider.id;
            const display = provider.state?.displayValues || {};
            const fields = provider.fields || [];

            return (
              <div
                key={provider.id}
                className="hub-card hub-enter-panel animate-fade-in-up"
                data-panel-index={index}
                style={{ marginBottom: 'var(--hub-space-xl)' }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 'var(--hub-space-md)',
                  marginBottom: 'var(--hub-space-xl)',
                  flexWrap: 'wrap',
                }}>
                  <div>
                    <h2 style={{
                      fontFamily: 'var(--hub-font-display)',
                      fontSize: '1.125rem',
                      fontWeight: 700,
                      marginBottom: 'var(--hub-space-xs)',
                    }}>
                      {provider.title}
                    </h2>
                    <p style={{ color: 'var(--hub-text-muted)', fontSize: '0.875rem', margin: 0 }}>
                      {provider.description}
                    </p>
                  </div>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '4px 10px',
                    borderRadius: 'var(--hub-radius-md)',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    background: statusOk
                      ? 'var(--hub-success-light)'
                      : (statusWarn ? 'var(--hub-warning-light, #FEF3C7)' : 'var(--hub-bg-tertiary)'),
                    color: statusOk
                      ? 'var(--hub-success)'
                      : (statusWarn ? 'var(--hub-warning, #B45309)' : 'var(--hub-text-primary)'),
                  }}>
                    {statusLabel}
                  </span>
                </div>

                {configured && (
                  <div style={{
                    background: 'var(--hub-bg-tertiary)',
                    padding: 'var(--hub-space-md)',
                    borderRadius: 'var(--hub-radius-md)',
                    marginBottom: 'var(--hub-space-xl)',
                    fontSize: '0.875rem',
                  }}>
                    {fields.filter((f) => !f.secret).map((field) => {
                      const value = display[field.key];
                      if (value == null || value === '' || typeof value === 'boolean') return null;
                      return (
                        <div key={field.key} style={{ marginBottom: '4px' }}>
                          <span style={{ color: 'var(--hub-text-muted)' }}>{field.label}: </span>
                          <code style={{ fontFamily: 'var(--hub-font-mono)' }}>{String(value)}</code>
                        </div>
                      );
                    })}
                    {fields.filter((f) => f.secret).map((field) => {
                      const last4 = display[`${field.key}Last4`] || (field.key === 'accessKey' ? display.accessKeyLast4 : '');
                      const hasSecret = Boolean(display.hasAccessKey || last4);
                      if (!hasSecret) return null;
                      return (
                        <div key={field.key}>
                          <span style={{ color: 'var(--hub-text-muted)' }}>{field.label}: </span>
                          <code style={{ fontFamily: 'var(--hub-font-mono)' }}>
                            ••••••••{last4 || ''}
                          </code>
                        </div>
                      );
                    })}
                    {provider.state?.lastVerifiedAt && (
                      <div style={{ marginTop: '4px', color: 'var(--hub-text-muted)' }}>
                        最終確認:{' '}
                        {new Date(provider.state.lastVerifiedAt).toLocaleString('ja-JP', {
                          timeZone: 'Asia/Tokyo',
                        })}
                      </div>
                    )}
                  </div>
                )}

                <form onSubmit={(e) => handleSave(provider, e)}>
                  {fields.filter((field) => SUPPORTED_FIELD_TYPES.has(field.type)).map((field) => {
                    if (field.type === 'select') {
                      return (
                        <div className="hub-form-group" key={field.key}>
                          <label className="hub-form-label" htmlFor={`${provider.id}-${field.key}`}>
                            {field.label}
                          </label>
                          <select
                            id={`${provider.id}-${field.key}`}
                            className="hub-form-input"
                            value={draft[field.key] || ''}
                            onChange={(e) => handleDraftChange(provider.id, field.key, e.target.value)}
                            disabled={!canManage || busy}
                            required={field.required || (!configured && field.requiredOnCreate)}
                          >
                            <option value="">選択してください</option>
                            {(field.options || []).map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      );
                    }

                    const inputType = field.type === 'password' ? 'password' : 'text';
                    return (
                      <div className="hub-form-group" key={field.key}>
                        <label className="hub-form-label" htmlFor={`${provider.id}-${field.key}`}>
                          {field.label}
                        </label>
                        <input
                          id={`${provider.id}-${field.key}`}
                          type={inputType}
                          className="hub-form-input"
                          value={draft[field.key] || ''}
                          onChange={(e) => handleDraftChange(provider.id, field.key, e.target.value)}
                          placeholder={fieldPlaceholder(field, configured)}
                          autoComplete={field.secret ? 'new-password' : 'off'}
                          disabled={!canManage || busy}
                          required={Boolean(field.required || (!configured && field.requiredOnCreate))}
                        />
                        {field.helpText && (
                          <p style={{ fontSize: '0.75rem', color: 'var(--hub-text-muted)', marginTop: 4 }}>
                            {field.helpText}
                          </p>
                        )}
                      </div>
                    );
                  })}

                  {canManage && (
                    <div style={{
                      display: 'flex',
                      gap: 'var(--hub-space-md)',
                      flexWrap: 'wrap',
                      marginTop: 'var(--hub-space-lg)',
                    }}>
                      <button type="submit" className="hub-btn hub-btn-primary" disabled={busy}>
                        {isSaving ? <LoadingSpinner size="small" /> : '保存して連携確認'}
                      </button>
                      {configured && (
                        <button
                          type="button"
                          className="hub-btn hub-btn-secondary"
                          onClick={() => setDisconnectTarget(provider)}
                          disabled={busy}
                        >
                          連携を解除
                        </button>
                      )}
                    </div>
                  )}
                </form>
              </div>
            );
          })
        )}

        {disconnectTarget && canManage && (
          <div
            className="hub-modal-overlay"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
            onClick={() => disconnectingId == null && setDisconnectTarget(null)}
          >
            <div
              className="hub-card"
              style={{
                width: '100%',
                maxWidth: '480px',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.7) 50%, rgba(255, 255, 255, 0.8) 100%)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{
                fontFamily: 'var(--hub-font-display)',
                fontSize: '1.25rem',
                fontWeight: 700,
                marginBottom: 'var(--hub-space-md)',
              }}>
                {disconnectTarget.title} 連携を解除しますか？
              </h2>
              <p style={{ color: 'var(--hub-text-secondary)', marginBottom: 'var(--hub-space-lg)' }}>
                保存済みの秘密情報は削除されます。この操作は取り消せません。
              </p>
              <div style={{ display: 'flex', gap: 'var(--hub-space-md)', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="hub-btn hub-btn-ghost"
                  onClick={() => setDisconnectTarget(null)}
                  disabled={disconnectingId != null}
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  className="hub-btn"
                  style={{ background: 'var(--hub-danger)', color: '#fff' }}
                  onClick={handleConfirmDisconnect}
                  disabled={disconnectingId != null}
                >
                  {disconnectingId != null ? <LoadingSpinner size="small" /> : '連携を解除'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AppIntegrations;
